import { useState } from "react";
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  WriteConfigurationScopeRequestSchema,
  type ConfigurationScopeSummary,
  type WriteConfigurationScopeRequest,
} from "@thalamos/haven-common";

import { asChains, chainChoices, entriesBelow } from "./configurationChains.js";
import type { ScopeFormOutcome } from "./scopeWrites.js";

export type ConfigurationScopeFormProps = {
  scopes: readonly ConfigurationScopeSummary[];
  /** The configuration being renamed or moved, or null when one is being created. */
  editing: ConfigurationScopeSummary | null;
  hint: string;
  onSubmit: (
    body: WriteConfigurationScopeRequest,
  ) => Promisable<ScopeFormOutcome>;
  onCancel: () => Promisable<void>;
};

/** A configuration's name and what it sits under. */
export const ConfigurationScopeForm = ({
  scopes,
  editing,
  hint,
  onSubmit,
  onCancel,
}: ConfigurationScopeFormProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(editing?.name ?? "");
  const [parentScopeId, setParentScopeId] = useState<string>(
    editing?.parentScopeId ?? "",
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A configuration cannot sit under itself or anything below it, so those are
  // not offered; the server refuses them too, and its check is the one that
  // decides.
  const chains = asChains(scopes);
  const excluded = new Set(
    editing === null
      ? []
      : chains.flatMap((chain) => [
          editing.id,
          ...entriesBelow({ chain, scopeId: editing.id }).map(
            (entry) => entry.id,
          ),
        ]),
  );
  const candidates = chainChoices({ chains, t }).filter(
    (choice) => !excluded.has(choice.id),
  );

  const submit = async () => {
    const parsed = WriteConfigurationScopeRequestSchema.safeParse({
      name,
      parentScopeId: parentScopeId === "" ? null : parentScopeId,
    });

    if (!parsed.success) {
      setError(
        t(
          // A failed parse always has an issue; the fallback is kept for the
          // index's type.
          /* v8 ignore next */
          parsed.error.issues[0]?.message ??
            "admin.configuration.scope.saveFailed",
        ),
      );
      return;
    }

    setSaving(true);

    const outcome = await onSubmit(parsed.data);

    setSaving(false);

    // The server's message names what it refused, and is the whole reason it
    // refuses rather than silently ignoring the value.
    match(outcome)
      .with({ kind: "refused" }, ({ message }) => {
        setError(message ?? t("admin.configuration.scope.saveFailed"));
      })
      .with({ kind: "saved" }, { kind: "awaiting_confirmation" }, () => {
        setError(null);
      })
      .exhaustive();
  };

  return (
    <Stack
      component="form"
      spacing={2}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {error !== null && <Alert severity="error">{error}</Alert>}

      <TextField
        label={t("admin.configuration.scope.name.label")}
        placeholder={t("admin.configuration.scope.name.placeholder")}
        value={name}
        onChange={(event) => {
          setName(event.target.value);
        }}
        size="small"
        fullWidth
      />

      <TextField
        select
        size="small"
        fullWidth
        label={t("admin.configuration.scope.parent.label")}
        value={parentScopeId}
        slotProps={{
          select: { displayEmpty: true },
          // The empty value displays the no-parent option, so keep the label
          // raised above it.
          inputLabel: { shrink: true },
        }}
        onChange={(event) => {
          setParentScopeId(event.target.value);
        }}
      >
        <MenuItem value="">
          {t("admin.configuration.scope.parent.none")}
        </MenuItem>
        {candidates.map((candidate) => (
          <MenuItem key={candidate.id} value={candidate.id}>
            {candidate.label}
          </MenuItem>
        ))}
      </TextField>

      <Typography variant="body2" color="text.secondary">
        {hint}
      </Typography>

      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={() => {
            void onCancel();
          }}
          disabled={saving}
        >
          {t("admin.configuration.scope.cancel")}
        </Button>
        <Button type="submit" variant="contained" disabled={saving}>
          {editing === null
            ? t("admin.configuration.scope.create")
            : t("admin.configuration.scope.save")}
        </Button>
      </Stack>
    </Stack>
  );
};
